import React, { useState, useEffect } from 'react'
import numeral from 'numeral'
import { Classes, FormGroup, InputGroup, Spinner } from '@blueprintjs/core'
import { Popover } from '@ui'
import api from '@api'
import track from '@track'
import { ICostBuilding } from '@api/cost/types'
import { IconButton, Button, NewGridTable, GridTableStatusEnum, IGridTableColumn } from '@ui'
import { ICost } from './hooks/useCost'
import { ITab } from '.'
import classnames from 'classnames'

interface Props {
  buildings: ICostBuilding[]
  cost: ICost
  tab: ITab
}

interface ITotal {
  id: string
  name: string
  annualTotal: number
  annualLease: number
  annualOps: number
}

export default function CostTable({ cost, buildings }: Props) {
  const columns: IGridTableColumn<ICostBuilding>[] = [
    {
      label: 'Building',
      Cell: ({ cell, skeleton }) => {
        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.name : 'placeholder'}
          </div>
        )
      },
    },
    {
      label: 'Total Cost',
      sort: 'costs.annualTotal',
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        if (!cell) return <div className={classnames({ [Classes.SKELETON]: skeleton })}>plchdr</div>

        return <div>{numeral(cell.data.costs.annualTotal).format('($0,0)')}</div>
      },
    },
    {
      label: 'Lease Cost ($)',
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        if (!cell) return <div className={classnames({ [Classes.SKELETON]: skeleton })}>plchdr</div>

        return <div>{numeral(cell.data.costs.annualLease).format('($0,0)')}</div>
      },
    },
    {
      label: 'Ops Cost ($)',
      headerStyle: {
        borderRight: '1px solid var(--border-color)',
      },
      cellStyle: {
        borderRight: '1px solid var(--border-color)',
      },
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        if (!cell) return <div className={classnames({ [Classes.SKELETON]: skeleton })}>plchdr</div>

        return <OpsCost {...{ building: cell.data, cost }} />
      },
    },
    {
      label: '$/SQFT',
      sort: 'costs.annualCostPerSqft',
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? numeral(cell.data.costs.annualCostPerSqft).format('($0,0)') : 'plchdr'}
          </div>
        )
      },
    },
    {
      label: '$/Seat',
      sort: 'costs.annualCostPerSeat',
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? numeral(cell.data.costs.annualCostPerSeat).format('($0,0)') : 'plchdr'}
          </div>
        )
      },
    },
    {
      label: '$/Seated Employee',
      sort: 'costs.annualCostPerEmployee',
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? numeral(cell.data.costs.annualCostPerEmployee).format('($0,0)') : 'plchdr'}
          </div>
        )
      },
    },
  ]

  const totalColumns: IGridTableColumn<ITotal>[] = [
    {
      label: 'Building',
      cellStyle: {
        fontWeight: 700,
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'var(--grey5)',
      } as React.CSSProperties,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{cell.data.name}</div>
      },
    },
    {
      label: 'Total Cost',
      alignRight: true,
      cellStyle: {
        fontWeight: 700,
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'var(--grey5)',
      } as React.CSSProperties,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{numeral(cell.data.annualTotal).format('$0,0.00a')}</div>
      },
    },
    {
      label: 'Lease Cost ($)',
      alignRight: true,
      cellStyle: {
        fontWeight: 700,
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'var(--grey5)',
      } as React.CSSProperties,
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <div data-cy="annualLeaseTotal">{numeral(cell.data.annualLease).format('$0,0.00a')}</div>
        )
      },
    },
    {
      label: 'Ops Cost ($)',
      cellStyle: {
        fontWeight: 700,
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'var(--grey5)',
      },
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{numeral(cell.data.annualOps).format('$0,0.00a')}</div>
      },
    },
    {
      label: '',
      colSpan: 3,
      cellStyle: {
        fontWeight: 700,
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'var(--grey5)',
      },
      Cell: () => {
        return null
      },
    },
  ]

  const formatBuildings = (items: ICostBuilding[]) => {
    return items.map((b) => ({
      data: b,
    }))
  }

  const formatedBuildings = formatBuildings(buildings)

  const totalData = [
    {
      data: {
        id: 'total',
        name: `Total (${buildings.length} bldgs)`,
        annualTotal: cost.annualTotal,
        annualLease: cost.annualLease,
        annualOps: cost.annualOps,
      },
    },
  ]

  return (
    <NewGridTable<ICostBuilding, ITotal>
      status={cost.isLoading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
      columns={columns}
      data={formatedBuildings}
      appendColumns={totalColumns}
      appendData={totalData}
      sort={{
        ...cost.sort,
        onSort: (column: string) => {
          track.cost({
            eventName: track.Event.SORT,
            props: {
              columnName: column,
            },
          })
          cost.setSort(column)
        },
      }}
      headerTop={50}
    />
  )
}

interface OpsCostProps {
  building: ICostBuilding
  cost: ICost
}
const ButtonStates: { [s: string]: JSX.Element | undefined } = {
  DEFAULT: undefined,
  LOADING: <Spinner size={15} />,
  EDITED: <IconButton minimal type="submit" iconName="check" />,
}
const OpsCost = ({ building, cost }: OpsCostProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [button, setButton] = useState<JSX.Element | undefined>(undefined)
  const [val, setVal] = useState(building.costs.annualOps + '')
  const [input, setInput] = useState<HTMLInputElement | null>(null)
  useEffect(() => {
    setVal(building.costs.annualOps + '')
  }, [building.costs.annualOps])

  const form = (
    <form
      style={{ maxWidth: 150 }}
      onSubmit={async (e) => {
        e.preventDefault()

        track.cost({
          eventName: track.Event.INLINE_ACTION,
          props: {
            actionName: 'Update OpsCost',
            actionValue: val,
          },
        })
        cost.updateCost({
          id: building.id,
          cost: { annualOps: val },
        })
        setButton(ButtonStates.LOADING)
        const id = building.costs.contractId
        const opsCost = val
        const input = {
          cost: opsCost,
          buildingId: building.id,
          year: cost.year,
        }
        const b = await (id ? api.cost.update({ id, ...input }) : api.cost.create(input))
        cost.setBuilding(b)
        setButton(ButtonStates.DEFAULT)
        setIsOpen(false)
      }}
    >
      <FormGroup label="Update Ops Cost" labelFor="opcost-input" style={{ margin: '10px' }}>
        <InputGroup
          id="opcost-input"
          value={numeral(val).format('$0,0')}
          inputRef={setInput}
          onChange={(e: any) => {
            setButton(ButtonStates.EDITED)
            setVal(e.target.value)
          }}
          rightElement={button}
        />
      </FormGroup>
    </form>
  )

  return (
    <Popover
      minimal
      isOpen={isOpen}
      content={form}
      position="top"
      onClose={() => {
        setVal(building.costs.annualOps + '')
        setButton(ButtonStates.DEFAULT)
        setIsOpen(false)
      }}
      onOpened={() => input && input.focus()}
    >
      <Button
        minimal
        onClick={() => setIsOpen(true)}
        style={{ border: '1px solid var(--border-color)' }}
        text={numeral(building.costs.annualOps).format('$0,0')}
      />
    </Popover>
  )
}
